import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BrainIcon, RepeatIcon, AlertTriangleIcon, KeyRoundIcon, UsersIcon, EyeIcon } from 'lucide-react';
const GOLD = '#d8b878';
const LAYERS = [{
  code: '01',
  icon: BrainIcon,
  text: 'Creencias heredadas que confundiste con tu identidad.'
}, {
  code: '02',
  icon: RepeatIcon,
  text: 'Patrones automáticos que se repiten sin que lo notes.'
}, {
  code: '03',
  icon: AlertTriangleIcon,
  text: 'Miedos invisibles que han dirigido decisiones importantes.'
}, {
  code: '04',
  icon: KeyRoundIcon,
  text: 'Reglas del sistema que aceptaste sin cuestionarlas.'
}, {
  code: '05',
  icon: UsersIcon,
  text: 'Relaciones y experiencias que actúan como espejos de conciencia.'
}, {
  code: '06',
  icon: EyeIcon,
  text: 'Una nueva forma de observar el juego con más claridad.'
}];
export function Revelation() {
  const container = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.55
      }
    }
  };
  return <section className="relative overflow-hidden bg-matrix-black py-20 md:py-28 px-5 md:px-8" data-id="element-299">
      {/* Digital console texture + depth */}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.045]" style={{
      backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,255,65,0.5) 0px, rgba(0,255,65,0.5) 1px, transparent 1px, transparent 26px), repeating-linear-gradient(90deg, rgba(216,184,120,0.4) 0px, rgba(216,184,120,0.4) 1px, transparent 1px, transparent 26px)',
      maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 80%)',
      WebkitMaskImage: 'radial-gradient(ellipse at center, black 20%, transparent 80%)'
    }} data-id="element-300"></div>
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_center,_rgba(216,184,120,0.06)_0%,_transparent_55%)]" data-id="element-301"></div>
      {/* Ambient depth from below + smooth transition in */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-2/3 bg-[radial-gradient(ellipse_at_bottom,_rgba(0,255,65,0.06)_0%,_transparent_60%)]" data-id="element-302"></div>
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-24 bg-gradient-to-b from-matrix-dark to-transparent" data-id="element-303"></div>

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-304">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-12 md:mb-16 max-w-2xl mx-auto" data-id="element-305">
          <span className="inline-block font-inter text-[10px] md:text-xs tracking-[0.4em] uppercase mb-5" style={{
          color: 'rgba(216,184,120,0.7)'
        }} data-id="element-306">
            Descodificando
          </span>
          <h2 className="font-cinzel text-3xl md:text-5xl text-matrix-cream mb-6 drop-shadow-[0_0_16px_rgba(245,230,200,0.15)]" data-id="element-307">
            El sistema descodificado
          </h2>
          <p className="text-[15px] md:text-lg text-matrix-white/80 font-light leading-[1.9]" data-id="element-308">
            Este ebook te invita a mirar más allá de la superficie y empezar a
            reconocer las estructuras invisibles que condicionan tu manera de
            pensar, sentir, decidir y vivir.
          </p>
        </motion.div>

        {/* Decoding layers */}
        <motion.div variants={container} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5" data-id="element-309">
          {LAYERS.map(({
          code,
          icon: Icon,
          text
        }, i) => <motion.div key={i} variants={item} whileHover={{
          y: -4,
          boxShadow: '0 0 34px -10px rgba(216,184,120,0.4)'
        }} className="group relative overflow-hidden rounded-xl border border-matrix-green/15 bg-matrix-dark/50 backdrop-blur-sm p-4 md:p-6 transition-colors duration-300 hover:border-[rgba(216,184,120,0.4)]" data-id="element-310">
              {/* Activation glow line at the top */}
              <motion.span className="absolute top-0 left-0 h-px w-full" style={{
            background: 'linear-gradient(90deg, transparent, rgba(216,184,120,0.7), transparent)'
          }} animate={{
            opacity: [0.25, 0.7, 0.25]
          }} transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.3
          }} data-id="element-311" />
              <div className="flex items-center justify-between mb-2.5 md:mb-4" data-id="element-312">
                <span className="flex h-8 w-8 md:h-10 md:w-10 items-center justify-center rounded-lg border border-[rgba(216,184,120,0.22)] bg-matrix-green/[0.06] transition-colors group-hover:border-[rgba(216,184,120,0.5)]" style={{
              color: GOLD
            }} data-id="element-313">
                  <Icon className="w-4 h-4 md:w-[19px] md:h-[19px]" strokeWidth={1.5} data-id="element-314" />
                </span>
                <span className="font-cinzel text-xs md:text-sm tracking-[0.2em] text-matrix-green/40" data-id="element-315">
                  {code}
                </span>
              </div>
              <p className="text-matrix-white/85 text-sm md:text-base font-light leading-snug md:leading-relaxed" data-id="element-316">
                {text}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Closing revelation */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.9,
        delay: 0.15
      }} className="mt-14 md:mt-20 mx-auto max-w-3xl text-center" data-id="element-317">
          <div className="w-px h-10 bg-gradient-to-b from-transparent via-[rgba(216,184,120,0.5)] to-transparent mx-auto mb-8" data-id="element-318"></div>
          <p className="font-cinzel text-xl sm:text-2xl md:text-3xl leading-[1.5] text-matrix-cream" style={{
          textShadow: '0 0 26px rgba(216,184,120,0.2)'
        }} data-id="element-319">
            No se trata solo de leer. Se trata de empezar a{' '}
            <span className="italic" style={{
            color: GOLD,
            textShadow: '0 0 28px rgba(216,184,120,0.45)'
          }} data-id="element-320">
              ver con más conciencia
            </span>{' '}
            lo que antes parecía normal.
          </p>
        </motion.div>
      </div>
    </section>;
}